import { z } from "zod";
import type { DomainErrorCode } from "@goppy/domain";

/**
 * US-IDM.F3-0864-S01 (@UC9522 · @UC0007) — MỘT nguồn cho mã + hình dạng lỗi validate ở biên HTTP.
 *
 * Trước task này, mọi lỗi zod ra `code: "BAD_REQUEST"` + `detail: "Validation failed"` (tiếng Anh) và
 * `errors[]` của thư viện bị bỏ — app không có gì để nói *ô nào* sai, nên người dùng Việt đọc câu Anh.
 *
 * QĐ người dùng 29/09/2026 (`index.md` §7 của task):
 *   · D1 — MỘT mã chung `VALIDATION_FAILED` (nằm trong `ERROR_CATALOG` ⇒ `x-error-catalog`).
 *   · D2 — `params` PHẲNG, chỉ issue ĐẦU TIÊN: `{ field, reason }`. KHÔNG đổi hình `ProblemDetail`.
 *   · D3 — bảng `reason` RIÊNG (`VALIDATION_REASONS`), KHÔNG chép `issue.code` của zod: hợp đồng wire
 *     không được đổi theo phiên bản thư viện (zod v4 đổi tên vài mã).
 *   · D4 — chỉ xuất `path` (khoá CÓ TRONG SCHEMA + chỉ số mảng; qua `z.record()`/`z.map()`/catchall
 *     cắt ở khoá gốc vì khoá con do người dùng đặt) + `reason` (+ `minimum`/`maximum` là SỐ của schema).
 *     🔴 KHÔNG BAO GIỜ `message`, `received`, tên khoá lạ, `params` của custom issue, `unionErrors`.
 *
 * Vì sao phải lọc path THEO SCHEMA chứ không theo hình dạng path: `["labels", "a@b.vn", "v"]` và
 * `["profile", "fullName"]` trông y hệt nhau — chỉ schema biết đoạn nào là tên ô do ta đặt, đoạn nào
 * là chuỗi người dùng gửi lên. Đường lỗi là đường ít được review nhất (docblock `paramsFromException`
 * của `ProblemDetailFilter`), nên mặc định là CẮT: không chắc ⇒ không xuất.
 *
 * File này phải an toàn cho trình duyệt (barrel bị `apps/admin` kéo vào bundle) — chỉ phụ thuộc zod.
 */

/** Mã lỗi validate — ổn định trên wire, ngôn-ngữ-trung-tính (CLAUDE.md §7). */
export const VALIDATION_FAILED_CODE = "VALIDATION_FAILED" as const satisfies DomainErrorCode;

/** Bảng `reason` đóng (D3). Thêm phần tử = đổi hợp đồng với client — đi kèm câu mobile. */
export const VALIDATION_REASONS = [
  "format",
  "too_short",
  "too_long",
  "required",
  "type",
  "enum",
  "unknown_field",
] as const;

export type ValidationReason = (typeof VALIDATION_REASONS)[number];

export const validationReasonSchema = z.enum(VALIDATION_REASONS);

/**
 * Một đoạn tên ô an toàn để đi ra ngoài: định danh kiểu JS. Khoá schema của repo đều khớp; khoá nào
 * không khớp thì CẮT ở đó (fail-closed) thay vì đoán nó có an toàn không.
 */
const SAFE_KEY_SEGMENT = /^[A-Za-z_][A-Za-z0-9_]*$/;

/** `field` = các đoạn an toàn nối bằng `.` (chỉ số mảng là số nguyên không âm). */
const FIELD_PATTERN = /^[A-Za-z_][A-Za-z0-9_]*(\.(\d+|[A-Za-z_][A-Za-z0-9_]*))*$/;

/**
 * `params` của response 400 `VALIDATION_FAILED`. `.strict()` ⇒ không trường nào chở văn bản tự do
 * hay giá trị input biểu diễn được. `field` VẮNG khi lỗi nằm ở gốc thân (vd khoá lạ ở gốc, thân
 * không phải object) — không đẻ `field: ""` gây hiểu nhầm.
 */
export const validationProblemParamsSchema = z
  .object({
    field: z.string().regex(FIELD_PATTERN).optional(),
    reason: validationReasonSchema,
    minimum: z.number().finite().optional(),
    maximum: z.number().finite().optional(),
  })
  .strict();

export type ValidationProblemParams = z.infer<typeof validationProblemParamsSchema>;

/**
 * `issue.code` của zod v3 → `reason`. `Record<z.ZodIssueCode, …>` ⇒ TS ĐÒI đủ mọi mã: zod thêm mã
 * mới thì typecheck đỏ ở đây, không lặng lẽ rơi về mặc định.
 */
const REASON_BY_ISSUE_CODE: Readonly<Record<z.ZodIssueCode, ValidationReason>> = {
  invalid_type: "type", // thiếu hẳn ⇒ `required` (xử lý riêng dưới)
  invalid_literal: "enum",
  custom: "format",
  invalid_union: "format",
  invalid_union_discriminator: "enum",
  invalid_enum_value: "enum",
  unrecognized_keys: "unknown_field",
  invalid_arguments: "type",
  invalid_return_type: "type",
  invalid_date: "format",
  invalid_string: "format",
  too_small: "too_short",
  too_big: "too_long",
  invalid_intersection_types: "type",
  not_multiple_of: "format",
  not_finite: "format",
};

/** Mã zod không có trong bảng (phiên bản lạ) ⇒ reason chung nhất, KHÔNG ném. */
const DEFAULT_REASON: ValidationReason = "format";

/** Thuần: một issue zod → một `reason` trong `VALIDATION_REASONS`. */
export function validationReasonForIssue(issue: z.ZodIssue): ValidationReason {
  // `received` ở đây là TÊN KIỂU zod suy ra ("undefined"/"string"…), chỉ đọc để phân loại —
  // không bao giờ đi ra ngoài.
  if (issue.code === "invalid_type" && (issue as { received?: unknown }).received === "undefined") {
    return "required";
  }
  return (
    (REASON_BY_ISSUE_CODE as Record<string, ValidationReason | undefined>)[issue.code] ??
    DEFAULT_REASON
  );
}

// ─── Lọc path theo schema (D4) ────────────────────────────────────────────────

type AnyZod = z.ZodTypeAny;
type PathSegment = string | number;

/** Chặn vòng vô hạn qua `z.lazy` tự tham chiếu. */
const MAX_UNWRAP_DEPTH = 32;

function defOf(schema: AnyZod): Record<string, unknown> & { typeName?: string } {
  return (schema as unknown as { _def: Record<string, unknown> & { typeName?: string } })._def;
}

/** Bóc mọi lớp bọc (optional/effects/union…) tới các schema "cụ thể" có thể chứa con. */
function concreteSchemas(schema: AnyZod, out: AnyZod[], depth = 0): void {
  if (depth > MAX_UNWRAP_DEPTH) return;
  const def = defOf(schema);
  const next = (s: unknown): void => {
    if (s && typeof s === "object" && "_def" in s) concreteSchemas(s as AnyZod, out, depth + 1);
  };
  switch (def.typeName) {
    case z.ZodFirstPartyTypeKind.ZodOptional:
    case z.ZodFirstPartyTypeKind.ZodNullable:
    case z.ZodFirstPartyTypeKind.ZodDefault:
    case z.ZodFirstPartyTypeKind.ZodCatch:
    case z.ZodFirstPartyTypeKind.ZodReadonly:
      next(def.innerType);
      return;
    case z.ZodFirstPartyTypeKind.ZodBranded:
    case z.ZodFirstPartyTypeKind.ZodPromise:
      next(def.type);
      return;
    case z.ZodFirstPartyTypeKind.ZodEffects:
      next(def.schema);
      return;
    case z.ZodFirstPartyTypeKind.ZodLazy:
      next((def.getter as () => unknown)());
      return;
    case z.ZodFirstPartyTypeKind.ZodPipeline:
      next(def.in);
      next(def.out);
      return;
    case z.ZodFirstPartyTypeKind.ZodUnion:
    case z.ZodFirstPartyTypeKind.ZodDiscriminatedUnion: {
      const options = def.options;
      const list = Array.isArray(options)
        ? options
        : options instanceof Map
          ? [...options.values()]
          : [];
      for (const o of list) next(o);
      return;
    }
    case z.ZodFirstPartyTypeKind.ZodIntersection:
      next(def.left);
      next(def.right);
      return;
    default:
      out.push(schema);
  }
}

/**
 * Con của một schema cụ thể theo đúng một đoạn path. Rỗng ⇒ đoạn này KHÔNG phải tên ô do schema khai
 * (khoá của record/map/catchall, khoá lạ, chỉ số vào thứ không phải mảng) ⇒ người gọi cắt tại đây.
 */
function childrenAt(schema: AnyZod, seg: PathSegment): AnyZod[] {
  const def = defOf(schema);
  switch (def.typeName) {
    case z.ZodFirstPartyTypeKind.ZodObject: {
      if (typeof seg !== "string") return [];
      const shape = (def.shape as () => Record<string, AnyZod>)();
      return Object.prototype.hasOwnProperty.call(shape, seg) ? [shape[seg]!] : [];
    }
    case z.ZodFirstPartyTypeKind.ZodArray:
      return typeof seg === "number" ? [def.type as AnyZod] : [];
    case z.ZodFirstPartyTypeKind.ZodSet:
      return typeof seg === "number" ? [def.valueType as AnyZod] : [];
    case z.ZodFirstPartyTypeKind.ZodTuple: {
      if (typeof seg !== "number") return [];
      const item = (def.items as AnyZod[])[seg] ?? (def.rest as AnyZod | null);
      return item ? [item] : [];
    }
    // ZodRecord / ZodMap / mọi kiểu lá: khoá con do NGƯỜI DÙNG đặt, hoặc không có con.
    default:
      return [];
  }
}

function isSafeSegment(seg: PathSegment): boolean {
  if (typeof seg === "number") return Number.isInteger(seg) && seg >= 0;
  return SAFE_KEY_SEGMENT.test(seg);
}

/**
 * Tiền tố dài nhất của `path` mà MỌI đoạn đều là khoá schema khai (hoặc chỉ số mảng). Dừng ở đoạn
 * đầu tiên không chứng minh được — không bao giờ bỏ qua một đoạn rồi đi tiếp.
 */
export function schemaFieldPath(schema: AnyZod, path: readonly PathSegment[]): PathSegment[] {
  let current: AnyZod[] = [schema];
  const out: PathSegment[] = [];
  for (const seg of path) {
    if (!isSafeSegment(seg)) break;
    const concrete: AnyZod[] = [];
    for (const c of current) concreteSchemas(c, concrete);
    const next = concrete.flatMap((c) => childrenAt(c, seg));
    if (next.length === 0) break;
    out.push(seg);
    current = next;
  }
  return out;
}

// ─── Dựng params ──────────────────────────────────────────────────────────────

/** `minimum`/`maximum` chỉ có nghĩa cho độ dài chuỗi/mảng/tập và giá trị số. */
const BOUND_TYPES = new Set(["string", "number", "array", "set"]);

function firstIssue(error: unknown): z.ZodIssue | null {
  if (!error || typeof error !== "object") return null;
  const issues = (error as { issues?: unknown }).issues;
  if (!Array.isArray(issues) || issues.length === 0) return null;
  const issue = issues[0] as { code?: unknown; path?: unknown } | null;
  if (!issue || typeof issue.code !== "string" || !Array.isArray(issue.path)) return null;
  return issue as z.ZodIssue;
}

function boundOf(issue: z.ZodIssue, key: "minimum" | "maximum"): number | undefined {
  const raw = (issue as unknown as Record<string, unknown>)[key];
  const type = (issue as unknown as { type?: unknown }).type;
  if (typeof raw !== "number" || !Number.isFinite(raw)) return undefined;
  return typeof type === "string" && BOUND_TYPES.has(type) ? raw : undefined;
}

/**
 * Lỗi validate (thường là `ZodError`) + schema đã dùng → `params` an toàn để đi ra response.
 *
 * Lấy ĐÚNG issue đầu tiên (D2). Không phải ZodError (transform ném lỗi thường) ⇒ `{ reason: "format" }`
 * — vẫn đủ để client hiện câu chung, không đoán thêm.
 */
export function validationProblemParams(error: unknown, schema: AnyZod): ValidationProblemParams {
  const issue = firstIssue(error);
  if (!issue) return { reason: DEFAULT_REASON };

  const reason = validationReasonForIssue(issue);
  const fieldPath = schemaFieldPath(schema, issue.path);
  const minimum = issue.code === "too_small" ? boundOf(issue, "minimum") : undefined;
  const maximum = issue.code === "too_big" ? boundOf(issue, "maximum") : undefined;

  const candidate: ValidationProblemParams = {
    ...(fieldPath.length > 0 ? { field: fieldPath.join(".") } : {}),
    reason,
    ...(minimum !== undefined ? { minimum } : {}),
    ...(maximum !== undefined ? { maximum } : {}),
  };
  // Chốt cuối: thứ đi ra PHẢI biểu diễn được bằng schema `.strict()` ở trên. Không khớp ⇒ chỉ reason.
  return validationProblemParamsSchema.safeParse(candidate).success ? candidate : { reason };
}
